import { Injectable } from '@angular/core';

import { LocalStorageService } from '@recruitee/common';

import { ALl_EMOJIS, EmojiCategory, FORCE_FALLBACK, SYMBOL_EMOJI_MAP } from './emoji-datasource';

export interface EmojiSupport {
  version: number;
  countryFlags: boolean;
}

interface EmojiSupportLS {
  expire: number;
  data: EmojiSupport;
}

@Injectable({ providedIn: 'root' })
export class EmojiSupportService {
  public readonly EMOJI_SUPPORT_KEY = 'EMOJI_SUPPORT';
  public emojiSupport: EmojiSupport;
  public fallbackRegex: RegExp | null;

  private emojiVersions = [0.6, 0.7, 1, 2, 3, 4, 5, 11, 12, 12.1, 13];

  constructor(private localStorage: LocalStorageService) {
    this.emojiSupport = this.getEmojiSupport();
    this.fallbackRegex = this.emojiFallbackRegexFactory();
  }

  public emojiFallbackRegexFactory(): RegExp | null {
    const pattern = ALl_EMOJIS.filter(emoji => !this.isEmojiSymbolSupported(emoji.symbol))
      .map(emoji => emoji.symbol)
      .sort((a, b) => b.length - a.length)
      .join('|')
      .replace(/\*/g, '\\*');

    if (!pattern) return null;

    return new RegExp(pattern, 'gu');
  }

  public getFallbackImgUrl(emojiSymbol: string): string | null {
    const item = SYMBOL_EMOJI_MAP[emojiSymbol];
    if (!item || !item.hasImgTwitter) return null;

    const fallback = item.unified.toLowerCase();
    return `assets/images/emoji/twitter/${fallback}.png`;
  }

  public isEmojiSymbolSupported(emojiSymbol: string) {
    const item = SYMBOL_EMOJI_MAP[emojiSymbol];
    if (!item) return false;

    if (FORCE_FALLBACK.includes(emojiSymbol)) return false;

    const isInSupportedRange = item.addedIn <= this.emojiSupport.version;
    if (item.category !== EmojiCategory.Flags) {
      return isInSupportedRange;
    }

    const isCountryFlag =
      item.shortName?.includes('flag-') || item.shortNames?.some(name => name.includes('flag-'));

    return isCountryFlag ? this.emojiSupport.countryFlags : isInSupportedRange;
  }

  private getEmojiSupport(): EmojiSupport {
    const date = new Date();
    const value: EmojiSupportLS | null = this.localStorage.get(this.EMOJI_SUPPORT_KEY, null, false);

    if (value && value.expire > date.getTime()) {
      return value.data;
    } else {
      const support = this.checkEmojiSupport();
      const notSupportedFromIndex = this.emojiVersions.findIndex(
        version => !(<any>support)[version],
      );
      const latestVersion = this.emojiVersions[this.emojiVersions.length - 1];
      const version =
        notSupportedFromIndex === -1
          ? latestVersion
          : this.emojiVersions[notSupportedFromIndex - 1] || 0;

      const data = {
        version,
        countryFlags: support.countryFlags,
      };
      const tomorrow = date.setDate(date.getDate() + 1);
      this.localStorage.set(
        this.EMOJI_SUPPORT_KEY,
        <EmojiSupportLS>{
          expire: tomorrow,
          data,
        },
        false,
      );

      return data;
    }
  }

  private sumRgb([r, g, b]: Uint8ClampedArray) {
    return r + g + b;
  }

  private testEmoji(emojiSymbol: string, _offsetX?: number, _offsetY?: number): boolean {
    const pixelRatio = window.devicePixelRatio || 1;
    const fontSize = 16 * pixelRatio;
    const offsetX = (_offsetX ?? 6) * pixelRatio;
    const offsetY = _offsetY ? _offsetY * pixelRatio : fontSize / 2 - 1;
    const node = document.createElement('canvas');
    const ctx = node.getContext('2d')!;

    ctx.fillStyle = '#f00';
    ctx.textBaseline = 'top';
    ctx.font = `${fontSize}px "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`;
    ctx.fillText(emojiSymbol, 0, 0);

    const { data: firstChar } = ctx.getImageData(offsetX, offsetY, 1, 1);
    const secondCharBeginSearch = fontSize + 4;
    let emptyPixel = true;
    let iter = 0;
    while (emptyPixel && iter < fontSize) {
      const { data } = ctx.getImageData(secondCharBeginSearch + iter, offsetY, 1, 1);
      emptyPixel = !this.sumRgb(data);
      iter++;
    }

    return this.sumRgb(firstChar) !== 0 && emptyPixel;
  }

  private checkEmojiSupport() {
    return {
      0.6: this.testEmoji('\u{1F198}'), // SOS
      0.7: this.testEmoji('\u{1F6E1}\u{FE0F}'), // shield
      1: this.testEmoji('\u{1F914}'), // thinking face
      2: this.testEmoji('\u{002A}\u{FE0F}\u{20E3}'), // keycap *
      3: this.testEmoji('\u{1F923}'), // rofl
      4: this.testEmoji('\u{1F645}\u{200D}\u{2640}\u{FE0F}'), // woman no
      5: this.testEmoji('\u{1F94C}'), // curling stone
      11: this.testEmoji('\u{1F94E}'), // softball
      12: this.testEmoji('\u{1F971}'), // yawn face
      12.1: this.testEmoji('\u{1F9D1}\u{200D}\u{1F33E}'), // farmer
      13: this.testEmoji('\u{1F978}'), // disguised Face
      countryFlags: this.testEmoji('\u{1F1FA}\u{1F1F8}', 4, 8), // flag US
    };
  }
}
